// frontend/src/pages/Permits/PermitList.jsx
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getPermits } from "../../api/permit.api.js";
import { PageHeader, Spinner } from "../../components/ui.jsx";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  HelpCircle,
  Search,
  Building2,
  Calendar,
} from "lucide-react";

const DOC_LABELS = {
  HAKI: "HAKI",
  NIB: "NIB",
  PKKPR: "PKKPR",
  SERTIFIKAT_HALAL: "Sertifikat Halal",
  PBG: "PBG",
  SLF: "SLF",
  LEGALITAS_LAHAN: "Legalitas Lahan",
  LAPORAN_RUPS_LUAR_BIASA: "Laporan RUPS Luar Biasa",
  LAPORAN_RUPS_TAHUNAN: "Laporan RUPS Tahunan",
};

function permitLabel(permit) {
  if (permit.type === "CUSTOM") return permit.customName ?? "Custom";
  return DOC_LABELS[permit.type] ?? permit.type;
}

function daysUntil(date) {
  if (!date) return null;
  const diff = new Date(date) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const STATUS_CONFIG = {
  expired: {
    label: "Kedaluwarsa",
    chipClass: "bg-red-100 text-red-700",
    rowClass: "bg-red-50 border-l-4 border-red-400",
    badgeClass: "bg-red-100 text-red-700",
    Icon: AlertTriangle,
  },
  expiring_soon: {
    label: "Segera Habis",
    chipClass: "bg-amber-100 text-amber-700",
    rowClass: "bg-amber-50 border-l-4 border-amber-400",
    badgeClass: "bg-amber-100 text-amber-700",
    Icon: Clock,
  },
  valid: {
    label: "Aktif",
    chipClass: "bg-green-100 text-green-700",
    rowClass: "",
    badgeClass: "bg-green-100 text-green-700",
    Icon: CheckCircle2,
  },
  no_date: {
    label: "Tanpa Tanggal",
    chipClass: "bg-gray-100 text-gray-500",
    rowClass: "",
    badgeClass: "bg-gray-100 text-gray-500",
    Icon: HelpCircle,
  },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status];
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.badgeClass}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  );
}

function StatCard({ status, count, active, onClick }) {
  const cfg = STATUS_CONFIG[status];
  const Icon = cfg.Icon;
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left ${
        active
          ? "border-brand-400 bg-brand-50 shadow-sm"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <div className={`p-2 rounded-lg ${cfg.chipClass}`}>
        <Icon size={16} />
      </div>
      <div>
        <p className="text-xl font-bold text-gray-900">{count}</p>
        <p className="text-xs text-gray-500">{cfg.label}</p>
      </div>
    </button>
  );
}

const FILTER_OPTIONS = [
  { key: "all", label: "Semua" },
  { key: "attention", label: "Perlu Perhatian" },
  { key: "valid", label: "Aktif" },
  { key: "no_date", label: "Tanpa Tanggal" },
];

export default function PermitList() {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const { data: permits = [], isLoading } = useQuery({
    queryKey: ["permits"],
    queryFn: () => getPermits(),
  });

  const counts = permits.reduce(
    (acc, p) => { acc[p.expiryStatus] = (acc[p.expiryStatus] ?? 0) + 1; return acc; },
    { expired: 0, expiring_soon: 0, valid: 0, no_date: 0 }
  );

  const filtered = permits.filter((p) => {
    if (filter === "attention") {
      if (p.expiryStatus !== "expired" && p.expiryStatus !== "expiring_soon") return false;
    } else if (filter !== "all") {
      if (p.expiryStatus !== filter) return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      const entityName = p.entityCache?.name?.toLowerCase() ?? "";
      const docType = permitLabel(p).toLowerCase();
      if (!entityName.includes(q) && !docType.includes(q)) return false;
    }
    return true;
  });

  return (
    <div>
      <PageHeader
        title="Daftar Perizinan"
        subtitle="Semua dokumen perizinan entitas — pantau masa berlaku"
      />

      {/* ── Stat cards ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {["expired", "expiring_soon", "valid", "no_date"].map((s) => (
          <StatCard
            key={s}
            status={s}
            count={counts[s]}
            active={filter === s || (filter === "attention" && (s === "expired" || s === "expiring_soon"))}
            onClick={() => setFilter(filter === s ? "all" : s)}
          />
        ))}
      </div>

      {/* ── Filter + search ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setFilter(opt.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                filter === opt.key
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {opt.label}
              {opt.key !== "all" && (
                <span className="ml-1 text-gray-400">
                  ({opt.key === "attention" ? counts.expired + counts.expiring_soon : counts[opt.key] ?? 0})
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="relative flex-1 max-w-xs">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="input pl-8 text-sm py-1.5"
            placeholder="Cari entitas atau tipe dokumen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <p className="text-xs text-gray-400 ml-auto">
          {filtered.length} dokumen
        </p>
      </div>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      {isLoading ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Calendar size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Tidak ada perizinan ditemukan</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Entitas
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Tipe Dokumen
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Berlaku s/d
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    File
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((permit) => {
                  const cfg = STATUS_CONFIG[permit.expiryStatus];
                  const days = daysUntil(permit.validUntil);
                  return (
                    <tr key={permit.id} className={`${cfg.rowClass} hover:bg-opacity-80 transition-colors`}>
                      <td className="px-4 py-3">
                        <Link
                          to={`/entities/${permit.entityCache?.id}`}
                          className="flex items-center gap-1.5 font-medium text-gray-900 hover:text-brand-600"
                        >
                          <Building2 size={13} className="text-gray-400 flex-shrink-0" />
                          {permit.entityCache?.name}
                          {permit.entityCache?.code && (
                            <span className="text-xs text-gray-400">· {permit.entityCache.code}</span>
                          )}
                        </Link>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {permitLabel(permit)}
                      </td>
                      <td className="px-4 py-3">
                        {permit.validUntil ? (
                          <div>
                            <p className={`font-medium ${
                              permit.expiryStatus === "expired" ? "text-red-700" :
                              permit.expiryStatus === "expiring_soon" ? "text-amber-700" :
                              "text-gray-800"
                            }`}>
                              {formatDate(permit.validUntil)}
                            </p>
                            {days !== null && (
                              <p className="text-xs mt-0.5 text-gray-500">
                                {days < 0
                                  ? `${Math.abs(days)} hari lalu`
                                  : days === 0
                                  ? "Hari ini"
                                  : `${days} hari lagi`}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs italic">Tidak diisi</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={permit.expiryStatus} />
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/entities/${permit.entityCache?.id}`}
                          className="text-xs text-brand-500 hover:text-brand-700 hover:underline truncate max-w-40 block"
                          title={permit.fileName}
                        >
                          {permit.fileName}
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
